import React from 'react';
import { Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { useNavigate, Link } from 'react-router-dom';
import './ProducaoGrid.css';

function ProducaoGrid({
    arrayProducao,
    clickDelete,
    formatarData,
    onConcluir,
    onEstornar,
    onCancelar,
    onAtualizarInsumos,
    userPermissions
}) {
    const navigate = useNavigate();

    // Função auxiliar para status class
    const getStatusClass = (status) => {
        switch (status) {
            case 'Planejada': return 'status-planejada';
            case 'Em Produção': return 'status-producao';
            case 'Concluído parcial': return 'status-parcial'; // Ajuste caso venha string diferente
            case 'Concluída parcial': return 'status-parcial';
            case 'Concluída': return 'status-concluida';
            case 'Cancelada': return 'status-cancelada';
            default: return 'status-secondary';
        }
    };

    if (!arrayProducao || arrayProducao.length === 0) {
        return (
            <div className="producao-empty-state">
                <div className="producao-empty-icon">
                    <i className="fas fa-industry"></i>
                </div>
                <h5 className="text-muted">Nenhuma Ordem de Produção</h5>
                <p className="text-muted small">
                    Não há OPs cadastradas ou que correspondam aos critérios da sua pesquisa.
                </p>
                <Link to="/app/producao/novo" className="empty-state-add-button">
                    <i className="fas fa-plus"></i>
                </Link>
            </div>
        );
    }

    return (
        <div className="producao-grid-container">
            <table className="table-custom">
                <thead>
                    <tr>
                        <th className="col-acoes-op text-center">Ações</th>
                        <th className="col-status-op text-start">Status</th>
                        <th className="col-num-op text-start">Número</th>
                        <th className="col-data-op text-start">Data</th>
                        <th className="col-produtos-op text-start">Produto</th>
                        <th className="col-descricao-op text-start">Descrição</th>
                        <th className="col-quantidade-op text-center">Quantidade</th>
                        <th className="col-quantidade-conc text-center">Concluído</th>
                        <th className="col-quantidade-aconc text-center">A Concluir</th>
                    </tr>
                </thead>
                <tbody>
                    {arrayProducao.map((op, index) => {
                        const totalPlanejado = Number(op.total_unidades) || 0;
                        const totalConcluido = Number(op.total_concluido) || 0;
                        const aConcluir = Math.max(0, totalPlanejado - totalConcluido);

                        // Flags
                        const podeConcluir = ['Planejada', 'Em Produção', 'Concluída parcial', 'Concluído parcial'].includes(op.status);
                        const podeEstornar = ['Concluída', 'Concluída parcial', 'Concluído parcial'].includes(op.status);
                        const podeCancelar = op.status === 'Planejada';
                        const podeExcluir = op.status === 'Planejada';
                        const podeAtualizarInsumos = op.status === 'Planejada'; // Assuming allowed for all Planned OPs in grid view since we lack insumo details
                        const showDropdown = podeConcluir || podeEstornar || podeCancelar || podeAtualizarInsumos;

                        // Dropdown direction logic
                        const totalRows = arrayProducao.length;
                        const isNearBottom = (totalRows - index) <= 4;

                        return (
                            <tr key={op.id}>
                                <td className="text-center td-actions" data-label="Ações">
                                    <div className="actions-cell">
                                        <button
                                            onClick={() => {
                                                if (userPermissions && userPermissions['editar_producao'] === false) {
                                                    Swal.fire({
                                                        icon: 'error',
                                                        title: 'Acesso Negado',
                                                        text: 'Sem permissão para editar.',
                                                        confirmButtonColor: '#2185c6'
                                                    });
                                                } else {
                                                    navigate(`/app/editar-producao/${op.id}`);
                                                }
                                            }}
                                            className="btn-action-sm btn-edit"
                                            title="Editar / Visualizar"
                                        >
                                            <i className="fas fa-edit"></i>
                                        </button>

                                        <button
                                            onClick={() => clickDelete(op.id)}
                                            className="btn-action-sm btn-delete"
                                            title={!podeExcluir ? 'Apenas OPs Planejadas podem ser excluídas' : 'Excluir'}
                                            disabled={!podeExcluir}
                                            style={{ opacity: !podeExcluir ? 0.5 : 1, cursor: !podeExcluir ? 'not-allowed' : 'pointer' }}
                                        >
                                            <i className="fas fa-trash"></i>
                                        </button>

                                        {showDropdown && (
                                            <Dropdown
                                                align="end" // Align end for better UX on right side
                                                drop={isNearBottom ? "up" : "down"}
                                            >
                                                <Dropdown.Toggle as="button" className="btn-action-sm btn-options" title="Mais Opções">
                                                    <i className="fas fa-ellipsis-v"></i>
                                                </Dropdown.Toggle>

                                                <Dropdown.Menu className="dropdown-menu-custom">
                                                    {podeConcluir && (
                                                        <Dropdown.Item className="action-item action-concluir" onClick={() => onConcluir && onConcluir(op.id)}>
                                                            <i className="fas fa-check-circle me-2"></i> Concluir Item
                                                        </Dropdown.Item>
                                                    )}
                                                    {podeEstornar && (
                                                        <Dropdown.Item className="action-item action-estornar" onClick={() => onEstornar && onEstornar(op.id)}>
                                                            <i className="fas fa-undo-alt me-2"></i> Estornar Conclusão
                                                        </Dropdown.Item>
                                                    )}
                                                    {podeAtualizarInsumos && (
                                                        <Dropdown.Item className="action-item action-atualizar" onClick={() => onAtualizarInsumos && onAtualizarInsumos(op.id)}>
                                                            <i className="fas fa-sync-alt me-2"></i> Atualizar Insumos
                                                        </Dropdown.Item>
                                                    )}
                                                    {podeCancelar && (
                                                        <>
                                                            <Dropdown.Divider />
                                                            <Dropdown.Item className="action-item action-cancelar" onClick={() => onCancelar && onCancelar(op.id)}>
                                                                <i className="fas fa-ban me-2"></i> Cancelar Produção
                                                            </Dropdown.Item>
                                                        </>
                                                    )}
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        )}
                                        {!showDropdown && <div style={{ width: '32px' }}></div>}
                                    </div>
                                </td>
                                <td className="text-start" data-label="Status">
                                    <span className={`producao-badge ${getStatusClass(op.status)}`}>
                                        {op.status}
                                    </span>
                                </td>
                                <td className="text-start" data-label="Número">{op.numero_op || op.numero}</td>
                                <td className="text-start" data-label="Data">{formatarData(op.data_producao)}</td>
                                <td className="text-start col-produtos-op" data-label="Produto">
                                    {op.produtos_info && op.produtos_info.length > 0 ? (
                                        op.produtos_info.length === 1 ? (
                                            <span className="text-muted small fw-bold">
                                                {op.produtos_info[0].descricao}
                                            </span>
                                        ) : (
                                            <OverlayTrigger
                                                placement="top"
                                                overlay={
                                                    <Tooltip id={`tooltip-prod-${op.id}`}>
                                                        <div className="text-start">
                                                            {op.produtos_info.map(p => (
                                                                <div key={p.id}>• {p.codigo} - {p.descricao}</div>
                                                            ))}
                                                        </div>
                                                    </Tooltip>
                                                }
                                            >
                                                <span className="badge bg-secondary" style={{ cursor: 'help' }}>
                                                    Diversos <i className="fas fa-info-circle ms-1"></i>
                                                </span>
                                            </OverlayTrigger>
                                        )
                                    ) : (
                                        <span className="text-muted small">-</span>
                                    )}
                                </td>
                                <td className="text-start col-descricao-cell" data-label="Descrição">{op.descricao || '—'}</td>
                                <td className="text-center fw-bold" data-label="Quantidade">{totalPlanejado}</td>
                                <td className="text-center fw-bold text-success" data-label="Concluído">{totalConcluido}</td>
                                <td className="text-center fw-bold text-info" data-label="A Concluir">{aConcluir}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

export default ProducaoGrid;
